import React, { type PropsWithChildren } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import type {
	AssociationEdge,
	AssociationEdgeEntity,
	ThirdPartyUser,
} from '@atlassian/search-client';
import { MentionPopup } from '@atlassian/search-ui/mention-popup';
import { PersonCard, useCopyToClipboard } from '@atlassian/search-ui/person-card';

import {
	getExternalMessageDmIntegrationInfo,
	getUserData,
} from '../../../utils/search-result-utils';

import { messages } from './messages';

/**
 * The whitespace in between aliases <!here> <!channel> is removed
 * This constant prevent the alias from being pushed together ex: "@here@channel"
 * We are using this constant rather than adding a space through CSS because it adds
 * a real space character that does not break up the text when selecting.
 * */
const SPACE = ' ';

export const MentionText = ({ children }: PropsWithChildren<unknown>): React.JSX.Element => {
	return (
		<span>
			{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
			{SPACE}@{children}
		</span>
	);
};

export type UserMentionProps = {
	children?: string[];
	associations?: AssociationEdge<AssociationEdgeEntity>[];
	integrationId?: string;
	allContributors?: ThirdPartyUser[];
};

export const UserMention = ({
	children,
	associations,
	integrationId,
	allContributors,
}: UserMentionProps): React.JSX.Element => {
	const userId = children?.[0];
	let name;
	let entity;
	if (fg('exclude_3p_entity_in_search_results')) {
		entity = allContributors?.find(
			(contributor): contributor is ThirdPartyUser =>
				Boolean(userId) && userId === contributor?.externalId,
		);
	} else {
		const userAssociation = associations?.find(
			(edge): edge is AssociationEdge<ThirdPartyUser> =>
				Boolean(userId) && userId === edge?.node?.entity?.externalId,
		);

		entity = userAssociation?.node?.entity;
	}
	name = entity?.nickname || entity?.name;

	const email = entity?.email;
	const copyToClipboard = useCopyToClipboard(email);

	if (entity && name) {
		const user = getUserData(entity);
		if (user) {
			return (
				<>
					{SPACE}
					<MentionPopup
						name={name}
						content={() => (
							<PersonCard
								onCopyEmail={copyToClipboard}
								user={user}
								externalMessageIntegration={getExternalMessageDmIntegrationInfo(
									integrationId,
									entity,
								)}
							/>
						)}
					/>
				</>
			);
		}
	}

	return (
		<MentionText>{name || <FormattedMessage {...messages.externalPersonLabel} />}</MentionText>
	);
};
